// posthog-js is dynamically imported so its ~20 KB gzip stays out of the
// main chunk for users who haven't opted in to analytics.

let initialized = false;
let initFailed = false;
let client: typeof import("posthog-js").default | null = null;

async function load(): Promise<typeof import("posthog-js").default> {
  if (client) return client;
  const mod = await import("posthog-js");
  client = mod.default;
  return client;
}

export async function initPostHog(): Promise<void> {
  if (initialized) return;
  const key = import.meta.env.VITE_POSTHOG_KEY;
  const host = import.meta.env.VITE_POSTHOG_HOST ?? "https://us.i.posthog.com";
  if (!key) {
    console.warn("[posthog] VITE_POSTHOG_KEY not set; analytics disabled");
    return;
  }
  try {
    const ph = await load();
    ph.init(key, {
      api_host: host,
      capture_pageview: true,
      persistence: "localStorage+cookie",
    });
    initialized = true;
    initFailed = false;
  } catch (err) {
    // Dynamic chunk blocked (Brave shields, uBlock, CSP), 4xx on a stale
    // hashed chunk after deploy, or PostHog init throwing on a bad key.
    // Best-effort telemetry: log + leave initialized=false so callers
    // short-circuit cleanly. We do NOT rethrow — the caller treats this
    // as fire-and-forget.
    initFailed = true;
    console.error("[posthog] init failed; analytics disabled", err);
    client = null;
  }
}

export async function optInAnalytics(): Promise<void> {
  await initPostHog();
  if (initialized && client) {
    client.opt_in_capturing();
  } else if (initFailed) {
    console.warn("[posthog] opt-in requested but init failed earlier; no capturing");
  }
}

export function optOutAnalytics(): void {
  if (initialized && client) client.opt_out_capturing();
}

export function isAnalyticsActive(): boolean {
  return initialized && !!client && !client.has_opted_out_capturing();
}
